import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { takeAllContentAzVNByType, updateContentAzVN } from '../../../../api/azumayavietnam/conentazvn';
import { showLoading, hideLoading } from '../../../../actions/ui';
import { informationToast, errorToast } from '../../../../utils/Toast/index';
import JsonTreeEditor from '../../../../components/JsonTreeEditor/JsonTreeEditor';
import './EditContentAzVN.css';

const LANGUAGES = ['en', 'ja', 'ko', 'vi'];

function EditContentAzVN() {
    const token = useSelector(state => state.token);
    const dispatch = useDispatch();

    const [type, setType] = useState('website');
    const [typeInput, setTypeInput] = useState('website');
    const [language, setLanguage] = useState('en');
    const [content, setContent] = useState({ en: {}, ja: {}, ko: {}, vi: {} });
    const [mode, setMode] = useState('tree'); // 'tree' | 'raw'
    const [rawText, setRawText] = useState('');
    const [rawError, setRawError] = useState('');
    const [saving, setSaving] = useState(false);

    const loadContent = async (contentType) => {
        try {
            dispatch(showLoading());
            const result = await takeAllContentAzVNByType(contentType, token);
            setContent(result.data.data);
            dispatch(hideLoading());
        } catch (error) {
            errorToast("Error when load content");
            dispatch(hideLoading());
        }
    };

    useEffect(() => {
        if (token) loadContent(type);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, type]);

    useEffect(() => {
        setRawText(JSON.stringify(content[language] || {}, null, 2));
        setRawError('');
    }, [language, content]);

    const handleTreeChange = (newValue) => {
        setContent((prev) => ({ ...prev, [language]: newValue }));
    };

    const commitType = () => {
        const nextType = typeInput.trim();
        if (nextType && nextType !== type) setType(nextType);
    };

    const applyRawText = () => {
        try {
            const parsed = JSON.parse(rawText);
            setContent((prev) => ({ ...prev, [language]: parsed }));
            setRawError('');
            return parsed;
        } catch (error) {
            setRawError('Invalid JSON: ' + error.message);
            return null;
        }
    };

    const handleSave = async () => {
        let dataToSave = content[language];

        if (mode === 'raw') {
            const parsed = applyRawText();
            if (parsed === null) return;
            dataToSave = parsed;
        }

        setSaving(true);
        try {
            dispatch(showLoading());
            await updateContentAzVN({ type, language, data: dataToSave }, token);
            informationToast(`Saved "${language}" content for type "${type}"`);
            dispatch(hideLoading());
        } catch (error) {
            errorToast(error.response?.data?.msg || error.message || "Error when save content");
            dispatch(hideLoading());
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="content-editor">
            <h1>Edit Content AZVN</h1>

            <div className="ce-toolbar">
                <label>
                    Type:{' '}
                    <input
                        className="jte-input"
                        value={typeInput}
                        onChange={(e) => setTypeInput(e.target.value)}
                        onBlur={commitType}
                    />
                </label>

                <div className="ce-lang-tabs">
                    {LANGUAGES.map((lng) => (
                        <button
                            key={lng}
                            type="button"
                            className={`ce-lang-tab ${language === lng ? 'active' : ''}`}
                            onClick={() => setLanguage(lng)}
                        >
                            {lng.toUpperCase()}
                        </button>
                    ))}
                </div>

                <div className="ce-mode-toggle">
                    <button
                        type="button"
                        className={mode === 'tree' ? 'active' : ''}
                        onClick={() => setMode('tree')}
                    >
                        Tree view
                    </button>
                    <button
                        type="button"
                        className={mode === 'raw' ? 'active' : ''}
                        onClick={() => setMode('raw')}
                    >
                        Raw JSON
                    </button>
                </div>

                <button type="button" className="ce-save-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>

            {mode === 'tree' ? (
                <div className="ce-tree-container">
                    <JsonTreeEditor
                        value={content[language] || {}}
                        onChange={handleTreeChange}
                        depth={0}
                    />
                </div>
            ) : (
                <div className="ce-raw-container">
                    <textarea
                        className="ce-raw-textarea"
                        value={rawText}
                        onChange={(e) => setRawText(e.target.value)}
                        onBlur={applyRawText}
                        spellCheck={false}
                    />
                    {rawError && <div className="ce-message ce-message-error">{rawError}</div>}
                </div>
            )}
        </div>
    );
}

export default EditContentAzVN;